import { useEffect, useState, type JSX } from 'react';
import type { PlaylistItem, StationSnapshot, Track } from '../lib/types';
import { useFavorites } from '../lib/useFavorites';
import { Dossier } from './Dossier';
import { ls } from '../lib/storage';
import { TrackSearch } from './TrackSearch';

interface DayLogProps {
  snap: StationSnapshot;
  onJump(uid: string): void;
  onOpenSettings(): void;
}

// module scope so it survives remounts (HMR/route swaps); PlaylistItem carries no
// timestamp or `say`, so we capture both the moment a track becomes nowPlaying.
const played = new Map<string, { at: number; say?: string }>();
const PLAYED_CAP = 200;
const HISTORY_CAP = 40;
const QUEUE_CAP = 60;

function recordPlayed(uid: string, startedAt: number, say: string | undefined): void {
  const existing = played.get(uid);
  if (existing && !(say && !existing.say)) return;
  played.set(uid, { at: startedAt || Date.now(), say });
  if (played.size > PLAYED_CAP) {
    const oldest = played.keys().next().value;
    if (oldest !== undefined) played.delete(oldest);
  }
}

function fmtTime(at: number): string {
  return new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function HeartIcon({ liked, stamping }: { liked: boolean; stamping: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-4 w-4 ${stamping ? 'stamp-in' : ''}`}
      fill={liked ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 13.5a7.7 7.7 0 0 0 0-3l1.9-1.5-2-3.4-2.3.7a7.6 7.6 0 0 0-2.6-1.5L14 2h-4l-.4 2.3a7.6 7.6 0 0 0-2.6 1.5l-2.3-.7-2 3.4L4.6 10.5a7.7 7.7 0 0 0 0 3L2.7 15l2 3.4 2.3-.7a7.6 7.6 0 0 0 2.6 1.5L10 22h4l.4-2.3a7.6 7.6 0 0 0 2.6-1.5l2.3.7 2-3.4-1.9-1.5Z" />
    </svg>
  );
}

interface RowProps {
  uid: string;
  title: string;
  artist: string;
  hhmm: string | null;
  isNow: boolean;
  isPast: boolean;
  resolving: boolean;
  say?: string;
  liked: boolean;
  stamping: boolean;
  onJump(uid: string): void;
  onStamp(e: React.MouseEvent, title: string, artist: string): void;
}

function Row({ uid, title, artist, hhmm, isNow, isPast, resolving, say, liked, stamping, onJump, onStamp }: RowProps) {
  return (
    <div
      onClick={() => onJump(uid)}
      className="group cursor-pointer border-b py-2"
      style={{ borderColor: 'color-mix(in srgb, var(--line) 60%, transparent)' }}
    >
      <div className="flex items-baseline gap-3">
        <span className="w-12 shrink-0 text-right text-xs tabular-nums text-faint">
          {isNow ? <span className="text-seal">♪</span> : hhmm ?? ''}
        </span>
        <span
          className={`min-w-0 flex-1 truncate ${
            isNow ? 'font-semibold text-fg' : isPast ? 'text-fg/45' : 'text-fg/80'
          }`}
        >
          {title}
          <span className="text-muted"> — {artist}</span>
        </span>
        {resolving && <span className="shrink-0 text-xs text-faint">解析中…</span>}
        <button
          aria-label={liked ? '取消收藏' : '收藏'}
          onClick={(e) => onStamp(e, title, artist)}
          className={`shrink-0 transition-opacity ${
            liked ? 'text-seal opacity-100' : 'text-faint opacity-30 group-hover:opacity-100'
          }`}
        >
          <HeartIcon liked={liked} stamping={stamping} />
        </button>
      </div>
      {say && (
        <p
          className="mt-0.5 pl-[3.75rem] text-sm text-muted"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {say}
        </p>
      )}
    </div>
  );
}

export function DayLog({ snap, onJump, onOpenSettings }: DayLogProps) {
  const [justStamped, setJustStamped] = useState<Set<string>>(new Set());
  const fav = useFavorites();

  const nowTrack = snap.nowPlaying?.track;
  const nowUid = nowTrack?.uid;
  useEffect(() => {
    if (!nowUid || !snap.nowPlaying) return;
    recordPlayed(nowUid, snap.nowPlaying.startedAt, snap.nowPlaying.track.say);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowUid, snap.nowPlaying?.track.say]);

  const stamp = (e: React.MouseEvent, title: string, artist: string, uid: string) => {
    e.stopPropagation();
    fav.toggle(title, artist);
    setJustStamped((prev) => new Set(prev).add(uid));
    setTimeout(() => {
      setJustStamped((prev) => {
        if (!prev.has(uid)) return prev;
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    }, 300);
  };

  const playlist: PlaylistItem[] = snap.playlist ?? [];
  const current = snap.current ?? -1;

  const historyAll = current > 0 ? playlist.slice(0, current) : [];
  const historyTrunc = Math.max(0, historyAll.length - HISTORY_CAP);
  const history = historyAll.slice(historyTrunc);

  const upcomingAll = current >= 0 ? playlist.slice(current + 1) : playlist.slice(0);
  const upcoming = upcomingAll.slice(0, QUEUE_CAP);
  const upcomingHidden = upcomingAll.length - upcoming.length;

  const nowItem = current >= 0 ? playlist[current] : undefined;

  const empty = playlist.length === 0;

  const timeline: JSX.Element[] = [];
  for (const it of history) {
    const rec = played.get(it.uid);
    const liked = fav.isLiked(it.title, it.artist);
    timeline.push(
      <Row
        key={it.uid}
        uid={it.uid}
        title={it.title}
        artist={it.artist}
        hhmm={rec ? fmtTime(rec.at) : null}
        isNow={false}
        isPast={true}
        resolving={it.status === 'resolving'}
        say={rec?.say}
        liked={liked}
        stamping={justStamped.has(it.uid)}
        onJump={onJump}
        onStamp={(e, t, a) => stamp(e, t, a, it.uid)}
      />,
    );
  }
  if (nowItem && snap.nowPlaying) {
    timeline.push(
      <div key={nowItem.uid}>
        <Row
          uid={nowItem.uid}
          title={nowItem.title}
          artist={nowItem.artist}
          hhmm={null}
          isNow={true}
          isPast={false}
          resolving={nowItem.status === 'resolving'}
          say={snap.nowPlaying.track.say}
          liked={fav.isLiked(nowItem.title, nowItem.artist)}
          stamping={justStamped.has(nowItem.uid)}
          onJump={onJump}
          onStamp={(e, t, a) => stamp(e, t, a, nowItem.uid)}
        />
        <div className="pl-[3.75rem]">
          <Dossier track={snap.nowPlaying.track} />
        </div>
      </div>,
    );
  }

  return (
    <section className="font-display px-5 pb-8 pt-4">
      <h2 className="text-xs tracking-[0.4em] text-muted">点歌</h2>
      <div className="mt-2 border-t bd" />
      <TrackSearch />

      <h2 className="text-xs tracking-[0.4em] text-muted">今日</h2>
      <div className="mt-2 border-t bd" />

      {empty ? (
        <p className="py-16 text-center text-sm text-faint">
          {snap.modePreparing ? '新模式歌单准备中…' : '节目志是空的 — 跟 DJ 说句话。'}
        </p>
      ) : (
        <>
          {historyTrunc > 0 && (
            <div className="py-2 text-center text-xs text-faint">…更早的 {historyTrunc} 首</div>
          )}
          {timeline}
          {snap.modePreparing && (
            <div className="animate-pulse border-b py-4 text-center text-sm text-faint bd">
              新模式歌单准备中…
            </div>
          )}

          {upcoming.map((it) => {
            const t: Track | undefined = snap.queue.find((q) => q.uid === it.uid);
            const liked = fav.isLiked(it.title, it.artist);
            return (
              <Row
                key={it.uid}
                uid={it.uid}
                title={it.title}
                artist={it.artist}
                hhmm={null}
                isNow={false}
                isPast={false}
                resolving={it.status === 'resolving'}
                say={t?.say}
                liked={liked}
                stamping={justStamped.has(it.uid)}
                onJump={onJump}
                onStamp={(e, t2, a) => stamp(e, t2, a, it.uid)}
              />
            );
          })}
          {upcomingHidden > 0 && (
            <div className="py-2 text-center text-xs text-faint">…还有 {upcomingHidden} 首</div>
          )}
        </>
      )}

      <div className="mt-16 flex items-center justify-center gap-6 text-faint">
        <button aria-label="设置" onClick={onOpenSettings} className="transition-colors hover:text-fg">
          <GearIcon className="h-4 w-4" />
        </button>
        <span aria-hidden="true">·</span>
        <button
          onClick={() => {
            const next = document.documentElement.classList.contains('light') ? 'dark' : 'light';
            document.documentElement.classList.remove('dark', 'light');
            document.documentElement.classList.add(next);
            ls.set('claudio.theme', next);
          }}
          className="text-xs tracking-[0.2em] transition-colors hover:text-fg"
        >
          浅色 / 深色
        </button>
      </div>
    </section>
  );
}
